/**
 * Filing Detail Modal Component
 *
 * Full-screen modal showing filing details with employee table.
 *
 * Session 3.2: Filing History Frontend
 */

import { useState, useEffect } from 'react';
import { FilingStatusBadge } from './FilingStatusBadge';
import type { FilingDetail, FilingEmployee } from '@/types/filing';
import {
  formatCurrency,
  formatEmployeeName,
  getCategoryLabel,
  FORMAT_TYPE_LABELS,
} from '@/types/filing';

interface FilingDetailModalProps {
  /** Filing detail data (null when loading) */
  filing: FilingDetail | null | undefined;
  /** Loading state */
  isLoading?: boolean;
  /** Close callback */
  onClose: () => void;
  /** Download callback */
  onDownload?: () => void;
  /** Mark as filed callback */
  onMarkAsFiled?: () => void;
  /** Update notes callback */
  onUpdateNotes?: (notes: string) => void;
  /** Whether download is in progress */
  isDownloading?: boolean;
  /** Whether mark as filed is in progress */
  isMarking?: boolean;
  /** Whether notes update is in progress */
  isUpdatingNotes?: boolean;
}

/**
 * Modal displaying full filing details.
 *
 * Features:
 * - Header with period and status badge
 * - Summary cards (Total Wages, Total Contributions, Employees)
 * - Editable notes section
 * - Employee table (NI#, Name, Wages, Gratuity, Contribution)
 * - Footer with Download C10 and Mark as Filed buttons
 */
export function FilingDetailModal({
  filing,
  isLoading = false,
  onClose,
  onDownload,
  onMarkAsFiled,
  onUpdateNotes,
  isDownloading = false,
  isMarking = false,
  isUpdatingNotes = false,
}: FilingDetailModalProps) {
  const [notes, setNotes] = useState(filing?.notes || '');
  const [isEditingNotes, setIsEditingNotes] = useState(false);

  // Sync notes state when filing changes - use key pattern instead of effect
  const filingNotesKey = filing?.notes ?? '';
  if (notes !== filingNotesKey && !isEditingNotes) {
    setNotes(filingNotesKey);
  }

  const handleSaveNotes = () => {
    onUpdateNotes?.(notes);
    setIsEditingNotes(false);
  };

  const handleCancelNotes = () => {
    setNotes(filing?.notes || '');
    setIsEditingNotes(false);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col">
          {isLoading ? (
            <ModalSkeleton onClose={onClose} />
          ) : filing ? (
            <>
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {filing.period_display}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {FORMAT_TYPE_LABELS[filing.format_type]} Format
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <FilingStatusBadge status={filing.status} />
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4">
                  <SummaryCard
                    title="Total Wages"
                    value={formatCurrency(filing.total_wages)}
                  />
                  <SummaryCard
                    title="Total Contributions"
                    value={formatCurrency(filing.total_contributions)}
                    highlight
                  />
                  <SummaryCard
                    title="Employees"
                    value={filing.employee_count.toString()}
                  />
                </div>

                {/* Notes Section */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900">Notes</h3>
                    {!isEditingNotes && (
                      <button
                        onClick={() => setIsEditingNotes(true)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {isEditingNotes ? (
                    <div className="space-y-2">
                      <textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                        rows={3}
                        placeholder="Add notes about this filing..."
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveNotes}
                          disabled={isUpdatingNotes}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {isUpdatingNotes ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelNotes}
                          disabled={isUpdatingNotes}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">
                      {filing.notes || (
                        <span className="italic text-gray-400">
                          No notes added
                        </span>
                      )}
                    </p>
                  )}
                </div>

                {/* Employee Table */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    Employees ({filing.employees.length})
                  </h3>
                  <EmployeeTable
                    employees={filing.employees}
                    isHospitality={filing.format_type === 'HOSPITALITY'}
                  />
                </div>

                {/* Timestamps */}
                <div className="text-xs text-gray-500 space-y-1">
                  <p>
                    Generated:{' '}
                    {new Date(filing.generated_at).toLocaleString('en-US', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </p>
                  {filing.filed_at && (
                    <p>
                      Filed:{' '}
                      {new Date(filing.filed_at).toLocaleString('en-US', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                    </p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Close
                </button>
                <div className="flex space-x-3">
                  <button
                    onClick={onDownload}
                    disabled={isDownloading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    {isDownloading ? 'Downloading...' : 'Download C10'}
                  </button>
                  {filing.status === 'GENERATED' && (
                    <button
                      onClick={onMarkAsFiled}
                      disabled={isMarking}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {isMarking ? 'Marking...' : 'Mark as Filed'}
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="p-6 text-center text-gray-500">
              Filing not found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface SummaryCardProps {
  title: string;
  value: string;
  highlight?: boolean;
}

function SummaryCard({ title, value, highlight = false }: SummaryCardProps) {
  return (
    <div
      className={`rounded-lg p-4 ${highlight ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50'}`}
    >
      <p className="text-sm text-gray-500">{title}</p>
      <p
        className={`text-lg font-semibold ${highlight ? 'text-blue-700' : 'text-gray-900'}`}
      >
        {value}
      </p>
    </div>
  );
}

interface EmployeeTableProps {
  employees: FilingEmployee[];
  isHospitality: boolean;
}

function EmployeeTable({ employees, isHospitality }: EmployeeTableProps) {
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              NI#
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Name
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Category
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Basic Wages
            </th>
            {isHospitality && (
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                Gratuity
              </th>
            )}
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Weeks
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
              Contribution
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {employees.map(employee => (
            <tr key={employee.id} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-sm font-mono text-gray-900">
                {employee.employee_ni}
              </td>
              <td className="px-4 py-2 text-sm text-gray-900">
                {formatEmployeeName(employee)}
              </td>
              <td className="px-4 py-2 text-sm text-gray-500">
                {getCategoryLabel(employee.category)}
              </td>
              <td className="px-4 py-2 text-sm text-gray-900 text-right">
                {formatCurrency(employee.basic_wages)}
              </td>
              {isHospitality && (
                <td className="px-4 py-2 text-sm text-gray-900 text-right">
                  {employee.gratuity ? formatCurrency(employee.gratuity) : '—'}
                </td>
              )}
              <td className="px-4 py-2 text-sm text-gray-500 text-right">
                {employee.weeks_worked ?? '—'}
              </td>
              <td className="px-4 py-2 text-sm font-medium text-gray-900 text-right">
                {formatCurrency(employee.total_contribution)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ModalSkeletonProps {
  onClose: () => void;
}

function ModalSkeleton({ onClose }: ModalSkeletonProps) {
  return (
    <div className="animate-pulse">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-6 bg-gray-200 rounded w-32" />
          <div className="h-4 bg-gray-100 rounded w-24" />
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="px-6 py-4 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-20" />
              <div className="h-6 bg-gray-300 rounded w-24" />
            </div>
          ))}
        </div>
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-16" />
          <div className="h-16 bg-gray-200 rounded" />
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-24" />
          <div className="h-32 bg-gray-100 rounded" />
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
        <div className="h-9 bg-gray-200 rounded w-20" />
        <div className="flex space-x-3">
          <div className="h-9 bg-gray-200 rounded w-32" />
          <div className="h-9 bg-gray-300 rounded w-28" />
        </div>
      </div>
    </div>
  );
}
